import { logger } from '@/lib/logger';

type PerfStatus = 'ok' | 'error';

type PerfEvent = {
  event: string;
  timestamp: number;
  durationMs?: number;
  status?: PerfStatus;
  meta?: Record<string, unknown>;
};

type FlowHandle = {
  id: string;
  name: string;
};

declare global {
  interface Window {
    __MEDIFLOW_PERF__?: PerfEvent[];
  }
}

const FLOW_PREFIX = 'mf_care_flow_active:';

function now(): number {
  return typeof performance !== 'undefined' ? performance.now() : Date.now();
}

function sanitizeMeta(meta?: Record<string, unknown>): Record<string, unknown> | undefined {
  if (!meta) return undefined;

  const deniedKeys = new Set(['email', 'password', 'firstName', 'lastName', 'phone', 'name']);
  const sanitized: Record<string, unknown> = {};

  for (const [key, value] of Object.entries(meta)) {
    if (deniedKeys.has(key)) continue;
    sanitized[key] = value;
  }

  return sanitized;
}

function emitPerf(event: PerfEvent): void {
  if (typeof window !== 'undefined') {
    window.__MEDIFLOW_PERF__ = window.__MEDIFLOW_PERF__ || [];
    window.__MEDIFLOW_PERF__.push(event);

    window.dispatchEvent(new CustomEvent('mediflow:perf', { detail: event }));
  }

  logger.info('[Perf]', event);
}

export function mark(name: string): void {
  if (typeof performance === 'undefined') return;
  performance.mark(name);
  emitPerf({ event: `mark:${name}`, timestamp: now() });
}

export function measure(name: string, startMark: string, endMark?: string): number | null {
  if (typeof performance === 'undefined') return null;

  try {
    performance.measure(name, startMark, endMark);
    const entries = performance.getEntriesByName(name, 'measure');
    const value = entries[entries.length - 1];
    if (!value) return null;

    emitPerf({
      event: `measure:${name}`,
      timestamp: now(),
      durationMs: Math.round(value.duration),
      status: 'ok',
    });

    return value.duration;
  } catch (error) {
    emitPerf({
      event: `measure:${name}`,
      timestamp: now(),
      status: 'error',
      meta: { reason: (error as Error)?.message || 'measure_failed' },
    });
    return null;
  }
}

export async function traceAsync<T>(
  label: string,
  fn: () => Promise<T>,
  meta?: Record<string, unknown>,
): Promise<T> {
  const startedAt = now();
  try {
    const result = await fn();
    emitPerf({
      event: `trace:${label}`,
      timestamp: now(),
      durationMs: Math.round(now() - startedAt),
      status: 'ok',
      meta: sanitizeMeta(meta),
    });
    return result;
  } catch (error) {
    emitPerf({
      event: `trace:${label}`,
      timestamp: now(),
      durationMs: Math.round(now() - startedAt),
      status: 'error',
      meta: sanitizeMeta({
        ...meta,
        reason: (error as Error)?.message || 'unknown_error',
      }),
    });
    throw error;
  }
}

export function startFlow(name: string): FlowHandle {
  const id = `${name}_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
  const handle: FlowHandle = { id, name };

  if (typeof sessionStorage !== 'undefined') {
    sessionStorage.setItem(`${FLOW_PREFIX}${name}`, id);
  }

  emitPerf({
    event: `flow:${name}:start`,
    timestamp: now(),
    status: 'ok',
    meta: { flowId: id },
  });

  return handle;
}

export function getActiveFlowId(name: string): string | null {
  if (typeof sessionStorage === 'undefined') return null;
  return sessionStorage.getItem(`${FLOW_PREFIX}${name}`);
}

export function flowStep(name: string, step: string, meta?: Record<string, unknown>): void {
  const flowId = getActiveFlowId(name);
  emitPerf({
    event: `flow:${name}:step:${step}`,
    timestamp: now(),
    status: 'ok',
    meta: sanitizeMeta({ flowId: flowId || 'none', ...meta }),
  });
}

export function endFlow(name: string, status: PerfStatus, meta?: Record<string, unknown>): void {
  const flowId = getActiveFlowId(name);

  emitPerf({
    event: `flow:${name}:end`,
    timestamp: now(),
    status,
    meta: sanitizeMeta({ flowId: flowId || 'none', ...meta }),
  });

  if (typeof sessionStorage !== 'undefined') {
    sessionStorage.removeItem(`${FLOW_PREFIX}${name}`);
  }
}

export async function initWebVitals(): Promise<void> {
  try {
    const { onCLS, onINP, onLCP, onTTFB } = await import('web-vitals');

    onCLS((metric) => {
      emitPerf({
        event: 'web-vital:CLS',
        timestamp: now(),
        durationMs: Number(metric.value.toFixed(4)),
        status: 'ok',
        meta: { rating: metric.rating },
      });
    });

    onINP((metric) => {
      emitPerf({
        event: 'web-vital:INP',
        timestamp: now(),
        durationMs: Math.round(metric.value),
        status: 'ok',
        meta: { rating: metric.rating },
      });
    });

    onLCP((metric) => {
      emitPerf({
        event: 'web-vital:LCP',
        timestamp: now(),
        durationMs: Math.round(metric.value),
        status: 'ok',
        meta: { rating: metric.rating },
      });
    });

    onTTFB((metric) => {
      emitPerf({
        event: 'web-vital:TTFB',
        timestamp: now(),
        durationMs: Math.round(metric.value),
        status: 'ok',
        meta: { rating: metric.rating },
      });
    });
  } catch (error) {
    emitPerf({
      event: 'web-vitals:init',
      timestamp: now(),
      status: 'error',
      meta: { reason: (error as Error)?.message || 'init_failed' },
    });
  }
}
